import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./NewsandEventsReadmore2.module.css";
import Gfooter from "../Component/Gfooter";

/* ── Variants ── */
const imgPanelVar = {
  hidden: { scale: 1.12, opacity: 0 },
  show:   { scale: 1,    opacity: 1, transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] } },
};

const rightVar = {
  hidden: { opacity: 0, x: 60 },
  show:   { opacity: 1, x: 0,  transition: { duration: 0.75, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const crumbVar   = { hidden: { opacity: 0, y: -12 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.15 } } };
const titleVar   = { hidden: { opacity: 0, y: 30  }, show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] } } };
const descVar    = { hidden: { opacity: 0, y: 20  }, show: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.38 } } };
const metaVar    = { hidden: { opacity: 0         }, show: { opacity: 1,        transition: { duration: 0.5, delay: 0.5  } } };

const galItemVar = {
  hidden: { opacity: 0, scale: 0.92 },
  show:   (i) => ({
    opacity: 1, scale: 1,
    transition: { duration: 0.5, delay: i * 0.07, ease: "easeOut" },
  }),
};

const lbVar    = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.2 } }, exit: { opacity: 0, transition: { duration: 0.16 } } };
const lbImgVar = { hidden: { opacity: 0, scale: 0.88 }, show: { opacity: 1, scale: 1, transition: { duration: 0.32, ease: "easeOut" } }, exit: { opacity: 0, scale: 0.88, transition: { duration: 0.2 } } };

/* ── Cell span class map ── */
const cellClass = [styles.gCell0, styles.gCell1, styles.gCell2, styles.gCell3, styles.gCell4];

function NewsEventsReadMore2() {
  const { id } = useParams();
  const [news, setNews]     = useState(null);
  const [imgs, setImgs]     = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchDetail = async () => {
      try {
        const res = await axios.post(
          "https://cyberathon.com/NLF/index.php/Api/Nlf_Controller/get_event_by_id",
          new URLSearchParams({ news_id: id }),
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        if (res.data.status === "true") {
          const d = res.data.data;
          setNews(d);
          setImgs(d.multi_news_image || []);
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (id) fetchDetail();
  }, [id]);

  /* keyboard nav */
  useEffect(() => {
    const onKey = (e) => {
      if (selected === null) return;
      if (e.key === "ArrowLeft")  setSelected(s => s === 0 ? fullImgs.length - 1 : s - 1);
      if (e.key === "ArrowRight") setSelected(s => s === fullImgs.length - 1 ? 0 : s + 1);
      if (e.key === "Escape")     setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  /* Loading */
  if (!news) {
    return (
      <div className={styles.loading}>
        {[0, 1, 2].map(i => (
          <motion.span
            key={i}
            className={styles.loadDot}
            animate={{ y: [0, -14, 0] }}
            transition={{ repeat: Infinity, duration: 0.85, delay: i * 0.18 }}
          />
        ))}
      </div>
    );
  }

  const base     = "https://cyberathon.com/NLF/";
  const heroImg  = news.news_image ? `${base}${news.news_image}` : null;
  const fullImgs = imgs.slice(0, 5).map(img => `${base}${img}`);
  const dateStr  = news.created_at ? new Date(news.created_at).toDateString() : "";
  const isMasonry = fullImgs.length === 5;

  return (
    <>
      <div className={styles.page}>

        {/* ══ SPLIT HERO ══ */}
        <div className={styles.heroSplit}>

          {/* LEFT — image */}
          <div className={styles.heroLeft}>
            {heroImg && (
              <motion.img
                src={heroImg}
                alt={news.news_heading}
                className={styles.heroImg}
                variants={imgPanelVar}
                initial="hidden"
                animate="show"
              />
            )}
            <div className={styles.heroImgLabel}>NLF · News &amp; Events</div>
          </div>

          {/* RIGHT — content */}
          <motion.div
            className={styles.heroRight}
            variants={rightVar}
            initial="hidden"
            animate="show"
          >
            <div className={styles.heroWatermark}>NLF</div>

            {/* Breadcrumb */}
            <motion.div className={styles.heroCrumb} variants={crumbVar} initial="hidden" animate="show">
              <Link to="/">Home</Link>
              <span className={styles.heroCrumbSep}>›</span>
              <Link to="/news-events">News &amp; Events</Link>
              <span className={styles.heroCrumbSep}>›</span>
              <span className={styles.heroCrumbActive}>Detail</span>
            </motion.div>

            {/* Title */}
            <motion.h1 className={styles.heroTitle} variants={titleVar} initial="hidden" animate="show">
              {news.news_heading}
            </motion.h1>

            {/* Description */}
            <motion.p className={styles.heroDesc} variants={descVar} initial="hidden" animate="show">
              {news.news_address}
            </motion.p>

            {/* Meta */}
            <motion.div className={styles.heroMeta} variants={metaVar} initial="hidden" animate="show">
              {dateStr && (
                <div className={styles.metaChip}>
                  <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  {dateStr}
                </div>
              )}
            </motion.div>
          </motion.div>
        </div>

        {/* ══ GALLERY ══ */}
        {fullImgs.length > 0 && (
          <div className={styles.galleryWrap}>

            {/* Header */}
            <motion.div
              className={styles.galleryHeader}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className={styles.galleryLabel}>Gallery</span>
              <span className={styles.galleryLine} />
              <span className={styles.galleryCount}>{fullImgs.length} Photos</span>
            </motion.div>

            {/* Grid */}
            <div className={isMasonry ? styles.galleryGrid : styles.galleryGridUniform}>
              {fullImgs.map((img, i) => (
                <motion.div
                  key={i}
                  className={`${styles.gItem} ${cellClass[i]}`}
                  custom={i}
                  variants={galItemVar}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  onClick={() => setSelected(i)}
                >
                  <img src={img} alt={`news-${i}`} />
                  <div className={styles.gItemOverlay}>
                    <div className={styles.gZoomBtn}>
                      <svg viewBox="0 0 24 24"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ══ LIGHTBOX ══ */}
      <AnimatePresence>
        {selected !== null && (
          <motion.div
            className={styles.lightbox}
            variants={lbVar} initial="hidden" animate="show" exit="exit"
            onClick={() => setSelected(null)}
          >
            <div className={styles.lbInner} onClick={e => e.stopPropagation()}>

              <div className={styles.lbCounter}>{selected + 1} / {fullImgs.length}</div>

              <button className={styles.lbClose} onClick={() => setSelected(null)}>✕</button>

              <button className={`${styles.lbArrow} ${styles.lbLeft}`}
                onClick={() => setSelected(s => s === 0 ? fullImgs.length - 1 : s - 1)}>❮</button>

              <AnimatePresence mode="wait">
                <motion.img
                  key={selected}
                  src={fullImgs[selected]}
                  alt="expanded"
                  className={styles.lbImg}
                  variants={lbImgVar} initial="hidden" animate="show" exit="exit"
                />
              </AnimatePresence>

              <button className={`${styles.lbArrow} ${styles.lbRight}`}
                onClick={() => setSelected(s => s === fullImgs.length - 1 ? 0 : s + 1)}>❯</button>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Gfooter />
    </>
  );
}

export default NewsEventsReadMore2;